'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Loader2, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

interface ConfirmButtonProps {
  /** Runs on the second click. May be async; a spinner shows while pending. */
  onConfirm: () => void | Promise<void>;
  /** Idle label, e.g. "Delete". */
  children: ReactNode;
  /** Label while armed. */
  confirmLabel?: string;
  icon?: LucideIcon;
  size?: 'md' | 'sm';
  disabled?: boolean;
  className?: string;
  title?: string;
}

/**
 * Two-click destructive action: first click arms it ("Sure?") for 3 seconds,
 * second click fires. Use this instead of a confirm modal for row-level and
 * inline destructive actions.
 */
export function ConfirmButton({
  onConfirm,
  children,
  confirmLabel = 'Sure?',
  icon: Icon,
  size = 'sm',
  disabled,
  className,
  title,
}: ConfirmButtonProps) {
  const [armed, setArmed] = useState(false);
  const [pending, setPending] = useState(false);
  const disarmTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (disarmTimer.current) clearTimeout(disarmTimer.current);
  }, []);

  const handleClick = async () => {
    if (pending) return;
    if (!armed) {
      setArmed(true);
      disarmTimer.current = setTimeout(() => setArmed(false), 3000);
      return;
    }
    if (disarmTimer.current) clearTimeout(disarmTimer.current);
    setArmed(false);
    try {
      setPending(true);
      await onConfirm();
    } finally {
      setPending(false);
    }
  };

  const iconClass = size === 'md' ? 'size-3.5' : 'size-3';
  return (
    <button
      type="button"
      disabled={disabled || pending}
      title={title}
      onClick={handleClick}
      className={cn(
        'corner-brand-sm inline-flex shrink-0 items-center justify-center gap-1.5 border font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        size === 'md' ? 'h-8 px-3 text-sm' : 'h-7 px-2.5 text-xs',
        'pointer-coarse:h-11',
        armed
          ? 'border-danger/60 bg-danger/10 text-danger hover:bg-danger/20'
          : 'border-zinc-700 text-zinc-400 hover:border-danger/60 hover:text-danger',
        className
      )}
    >
      {pending ? (
        <Loader2 className={cn(iconClass, 'animate-spin')} aria-hidden />
      ) : (
        Icon && <Icon className={iconClass} aria-hidden />
      )}
      {armed ? confirmLabel : children}
    </button>
  );
}
